import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddIcon from "@mui/icons-material/Add";
import CafeCard from "./CafeCard";
import CafeDetailModal from "./CafeDetailModal";
import CafeLoginPromptModal from "./CafeLoginPromptModal";
import CafeRegistrationModal from "./CafeRegistrationModal";
import MultiCarousel from "../../components/carousel/MultiCarousel";
import { subscribeCafeList } from "../../services/cafeService";
import { checkAdminPermission } from "../../services/adminAuth";
import { useThreadsAuth } from "../../hooks/useThreadsAuth";

/**
 * ThreadsCafeMapSection
 * 2025-09 전용 간단 지도 섹션 (Mapbox GL)
 * - 한국 중심, 최소 4색 팔레트
 * - 반응형 높이 적용
 */
const ThreadsCafeMapSection = ({
	participants = [],
	collections = [],
	showCafeRegistrationModal,
	setShowCafeRegistrationModal,
	isFull = false,
}) => {
	const theme = useTheme();
	const { user: currentUser, isLoggedIn } = useThreadsAuth();
	const wrapperRef = useRef(null);
	const mapContainerRef = useRef(null);
	const mapRef = useRef(null);
	const resizeObserverRef = useRef(null);
	const markersRef = useRef([]);
	const [cafes, setCafes] = useState([]);
	const cafesRef = useRef([]);
	const [mapReady, setMapReady] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(null);
	const [visibleCafes, setVisibleCafes] = useState([]);
	const [detailCafe, setDetailCafe] = useState(null);
	const [showLoginPromptModal, setShowLoginPromptModal] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const participantsByIdRef = useRef({});
	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

	// Breakpoint watchers to force map resize right after CSS layout changes
	const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
	const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

	// Check if current user is admin
	useEffect(() => {
		const checkAdmin = async () => {
			if (currentUser?.id) {
				const adminStatus = await checkAdminPermission(currentUser.id);
				setIsAdmin(adminStatus);
			} else {
				setIsAdmin(false);
			}
		};
		checkAdmin();
	}, [currentUser]);

	useEffect(() => {
		// participants 배열을 id 기준 매핑으로 보관 (userInfo 선택시 사용)
		const map = {};
		(participants || []).forEach((u) => {
			if (!u) return;
			const uid = String(u?.id || u?.user_id || u?.profile?.id || "");
			if (!uid) return;
			map[uid] = u;
		});
		participantsByIdRef.current = map;
	}, [participants]);

	// Measure wrapper size to ensure map strictly follows layout bounds
	useLayoutEffect(() => {
		const target = wrapperRef.current;
		if (!target) return;

		const updateSize = () => {
			const rect = target.getBoundingClientRect();
			const width = Math.max(0, Math.floor(rect.width));
			const height = Math.max(0, Math.floor(rect.height));
			setContainerSize((prev) =>
				prev.width === width && prev.height === height
					? prev
					: { width, height }
			);
		};

		updateSize();

		let ro = null;
		if ("ResizeObserver" in window) {
			try {
				ro = new ResizeObserver(updateSize);
				ro.observe(target);
			} catch (_) {
				ro = null;
			}
		} else {
			window.addEventListener("resize", updateSize);
		}

		return () => {
			if (ro) {
				try {
					ro.disconnect();
				} catch (_) {}
			} else {
				window.removeEventListener("resize", updateSize);
			}
		};
	}, []);

	// 현재 지도 뷰포트에 보이는 카페를 계산하여 상태로 반영
	const computeVisibleCafes = () => {
		try {
			const map = mapRef.current;
			if (!map) return;
			const b = map.getBounds();
			const source = cafesRef.current || [];
			const list = source.filter(
				(c) =>
					typeof c?.lat === "number" &&
					typeof c?.lng === "number" &&
					c.lat >= b.getSouth() &&
					c.lat <= b.getNorth() &&
					c.lng >= b.getWest() &&
					c.lng <= b.getEast()
			);
			setVisibleCafes(list);
		} catch (_) {}
	};

	useEffect(() => {
		// cafeList 실시간 구독 - admin은 모든 카페를 볼 수 있고, 일반 사용자는 승인된 카페만
		const unsub = subscribeCafeList(
			(list) => {
				const arr = Array.isArray(list) ? list : [];
				cafesRef.current = arr;
				setCafes(arr);
			},
			{
				includesPending: false,
				isAdmin: isAdmin,
			}
		);
		return () => {
			try {
				unsub && unsub();
			} catch (_) {}
		};
	}, [isAdmin]);

	// 카페 데이터가 갱신되거나 맵이 준비되면 즉시 가시 카페 재계산
	useEffect(() => {
		if (!mapReady || !mapRef.current) return;
		computeVisibleCafes();
	}, [cafes, mapReady]);

	useEffect(() => {
		if (!mapReady || !mapRef.current || containerSize.width <= 0) return;
		try {
			mapRef.current.resize();
		} catch (_) {}
	}, [mapReady, containerSize.width, containerSize.height]);

	useEffect(() => {
		// 윈도우 리사이즈 시 지도 리사이즈 및 가시 카페 재계산 (디바운스)
		if (!mapReady) return;
		let timer = null;
		const onWindowResize = () => {
			if (timer) {
				clearTimeout(timer);
			}
			timer = setTimeout(() => {
				try {
					if (mapRef.current) {
						mapRef.current.resize();
						computeVisibleCafes();
						try {
							setZoomLevel(mapRef.current.getZoom());
						} catch (_) {}
					}
				} catch (_) {}
			}, 120);
		};
		window.addEventListener("resize", onWindowResize);
		return () => {
			try {
				window.removeEventListener("resize", onWindowResize);
			} catch (_) {}
			if (timer) clearTimeout(timer);
		};
	}, [mapReady]);

	// 초기 렌더/모바일 뷰포트에서도 안정적으로 사이즈 반영: pageshow/orientationchange 대응
	useEffect(() => {
		if (!mapReady) return;
		const onPageShow = () => {
			try {
				mapRef.current && mapRef.current.resize();
				computeVisibleCafes();
			} catch (_) {}
		};
		const onOrientation = () => {
			setTimeout(() => {
				try {
					mapRef.current && mapRef.current.resize();
					computeVisibleCafes();
				} catch (_) {}
			}, 200);
		};
		window.addEventListener('pageshow', onPageShow);
		window.addEventListener('orientationchange', onOrientation);
		return () => {
			try { window.removeEventListener('pageshow', onPageShow); } catch (_) {}
			try { window.removeEventListener('orientationchange', onOrientation); } catch (_) {}
		};
	}, [mapReady]);

	// 레이아웃 안정화 전까지 몇 프레임 동안 resize를 반복 호출 (초기 모바일 뷰포트 대응)
	useEffect(() => {
		if (!mapReady) return;
		let attempts = 0;
		let lastWidth = 0;
		const tick = () => {
			const el = mapContainerRef.current;
			const w = el ? el.clientWidth : 0;
			if (w > 0 && w !== lastWidth) {
				lastWidth = w;
				try {
					mapRef.current && mapRef.current.resize();
					computeVisibleCafes();
				} catch (_) {}
			}
			if (++attempts < 20) {
				requestAnimationFrame(tick);
			}
		};
		requestAnimationFrame(tick);
	}, [mapReady, isSmDown, isMdDown]);

	// 브레이크포인트 전환 직후 1프레임 후 강제 리사이즈 (CSS 레이아웃 반영 보장)
	useEffect(() => {
		if (!mapRef.current) return;
		const t = setTimeout(() => {
			try {
				mapRef.current.resize();
				computeVisibleCafes();
			} catch (_) {}
		}, 0);
		return () => clearTimeout(t);
	}, [isSmDown, isMdDown]);

	useEffect(() => {
		// 마커 업데이트
		const map = mapRef.current;
		if (!map || !mapReady) return;
		// clear
		try {
			markersRef.current.forEach((m) => m.remove());
		} catch (_) {}
		markersRef.current = [];
		// add
		cafes.forEach((c) => {
			if (typeof c?.lng !== "number" || typeof c?.lat !== "number") return;
			const dense = typeof zoomLevel === "number" ? zoomLevel < 8 : false;
			const largeLabel = typeof zoomLevel === "number" ? zoomLevel >= 9 : false;
			const circleSize = dense ? Math.round(28 * 0.75) : 28;
			const wrapper = document.createElement("div");
			wrapper.style.display = "flex";
			wrapper.style.flexDirection = "column";
			wrapper.style.alignItems = "center";
			wrapper.style.transform = "translateY(0)";
			wrapper.style.pointerEvents = "auto";

			const circle = document.createElement("div");
			circle.style.width = circleSize + "px";
			circle.style.height = circleSize + "px";
			circle.style.borderRadius = "50%";
			circle.style.display = "flex";
			circle.style.alignItems = "center";
			circle.style.justifyContent = "center";
			circle.style.border = "1px solid transparent";

			// Cafe type based colors - unified to primary color
			if (c.approvalStatus === "pending") {
				// Pending cafes - yellow (only visible to admin)
				circle.style.background = "#FFC107";
				circle.style.color = "#000";
			} else {
				// All approved cafes use primary color
				circle.style.background = theme.palette.primary.main;
				circle.style.color = "#fff";
			}

			// 간단한 커피 아이콘 SVG (inline)
			// 미니멀 스트로크 커피 아이콘 (Phosphor 스타일 유사)
			circle.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 8h14v6a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
          <path d="M16 8h2a3 3 0 1 1 0 6h-2" />
          <path d="M7 2v2" /><path d="M11 2v2" /><path d="M15 2v2" />
        </svg>
      `;

			wrapper.appendChild(circle);
			// 라벨 반전 효과를 위한 참조 저장
			let labelEl = null;
			wrapper.style.cursor = "pointer";
			if (!dense) {
				const label = document.createElement("div");
				label.textContent = (c?.name || "").toString();
				label.style.marginTop = "4px";
				label.style.padding = "2px 6px";
				label.style.fontSize = largeLabel ? "13px" : "11px";
				label.style.lineHeight = "1.2";
				label.style.whiteSpace = "nowrap";
				label.style.borderRadius = "8px";
				label.style.border = `1px solid ${theme.palette.divider}`;
				label.style.background = theme.palette.background.paper;
				label.style.color = theme.palette.text.primary;
				// 라벨은 카페 이름만 표시 (유저네임 제거)
				wrapper.appendChild(label);
				labelEl = label;
			}

			// hover 시: 라벨 배경/전경 반전 효과 적용 (마커 원은 변화 없음)
			const onEnter = () => {
				if (labelEl) {
					labelEl.style.background = theme.palette.text.primary;
					labelEl.style.color = "#fff";
					labelEl.style.border = "1px solid transparent";
				}
			};
			const onLeave = () => {
				if (labelEl) {
					labelEl.style.background = theme.palette.background.paper;
					labelEl.style.color = theme.palette.text.primary;
					labelEl.style.border = `1px solid ${theme.palette.divider}`;
				}
			};
			wrapper.addEventListener("mouseenter", onEnter);
			wrapper.addEventListener("mouseleave", onLeave);

			// 클릭 시 상세 모달 오픈
			wrapper.addEventListener("click", () => {
				try {
					setDetailCafe(c);
				} catch (_) {}
			});

			const marker = new window.mapboxgl.Marker({
				element: wrapper,
				anchor: "bottom",
			})
				.setLngLat([c.lng, c.lat])
				.addTo(map);
			markersRef.current.push(marker);
		});
	}, [cafes, theme, mapReady, zoomLevel]);

	useEffect(() => {
		let isMounted = true;
		const containerEl = mapContainerRef.current;
		if (!containerEl || containerSize.width <= 0 || mapRef.current) {
			return () => {
				isMounted = false;
			};
		}
		let mapboxglModule = null;

		const init = async () => {
			try {
				const mod = await import("mapbox-gl");
				if (!isMounted) return;
				mapboxglModule = mod.default || mod;
				try {
					window.mapboxgl = mapboxglModule;
				} catch (_) {}
				// 토큰 우선순위: env -> window -> localStorage
				const runtimeToken =
					(typeof window !== "undefined" &&
						(window.REACT_APP_MAPBOX_TOKEN || window.MAPBOX_TOKEN)) ||
					(typeof localStorage !== "undefined" &&
						localStorage.getItem("MAPBOX_TOKEN")) ||
					process.env.REACT_APP_MAPBOX_TOKEN ||
					"";
				mapboxglModule.accessToken = runtimeToken;
				if (!runtimeToken) {
					// 토큰 없으면 지도 로드 중단
					return;
				}

				// 4색 팔레트
				const landColor = theme.palette.background.default;
				let waterColor = theme.palette.background.paper;
				// 땅/물 색이 동일하면 약간의 대비를 줘서 빈 박스로 보이지 않도록 처리
				if (waterColor === landColor) {
					waterColor =
						theme.palette.mode === "dark"
							? "rgba(255,255,255,0.06)"
							: "rgba(0,0,0,0.04)";
				}
				// const majorLine = theme.palette.primary.main;
				// const minorLine = theme.palette.primary.light; // 보조 경계선

				const baseStyleUrl =
					"mapbox://styles/groovelb/cljxxcngj000m01rjfwx7dd47";
				const styleUrl = `${baseStyleUrl}?t=${Date.now()}`; // 캐시 무효화 파라미터로 최신 스타일 강제 로드

				// Check if mobile
				const isMobileDevice = window.innerWidth <= 600;

				mapRef.current = new mapboxglModule.Map({
					container: containerEl,
					style: styleUrl,
					center: [127.7669, 36.2], // 대한민국 중심부 - 제주도가 보이도록 조정
					zoom: isMobileDevice ? 5.8 : 6.3, // 모바일에서 더 축소
					attributionControl: false,
				});

				// 페이지 스크롤과 충돌 방지를 위해 휠/트랙패드 스크롤 줌 비활성화
				try {
					mapRef.current.scrollZoom.disable();
				} catch (_) {}

				// 최소 상호작용 설정 (회전/피치 비활성화)
				mapRef.current.dragRotate.disable();
				mapRef.current.touchZoomRotate.disableRotation();

				// 기본 내비게이션 컨트롤(줌/컴파스) 추가 - 컴파스는 숨김
				try {
					const nav = new mapboxglModule.NavigationControl({
						showZoom: true,
						showCompass: false,
						visualizePitch: false,
					});
					mapRef.current.addControl(nav, "top-right");
				} catch (_) {}

				// 트랙패드 핀치(보통 ctrlKey 동반 wheel)만 허용하고 일반 스크롤은 페이지로 전달
				try {
					const wheelHandler = (e) => {
						try {
							if (e && e.ctrlKey) {
								// 트랙패드 핀치로 인식되는 경우만 줌 처리
								e.preventDefault();
								const map = mapRef.current;
								if (!map) return;
								// deltaY가 작을 수 있어 민감도를 약간 높임
								const zoomDelta = -e.deltaY * 0.006;
								const targetZoom = map.getZoom() + zoomDelta;
								map.zoomTo(targetZoom, { duration: 0 });
							}
							// ctrlKey가 아닌 일반 휠은 아무것도 하지 않아 페이지 스크롤로 흘려보냄
						} catch (_) {}
					};
					const canvasContainer =
						mapRef.current && mapRef.current.getCanvasContainer();
					if (canvasContainer) {
						canvasContainer.addEventListener("wheel", wheelHandler, {
							passive: false,
						});
					}
					// cleanup에 등록
					mapRef.current.__customWheelHandler = wheelHandler;
				} catch (_) {}

				// 줌 레벨 실시간 표시
				try {
					setZoomLevel(mapRef.current.getZoom());
					mapRef.current.on("zoom", () => {
						try {
							setZoomLevel(mapRef.current && mapRef.current.getZoom());
						} catch (_) {}
					});
					mapRef.current.on("moveend", () => {
						computeVisibleCafes();
					});
				} catch (_) {}

				// 한국어 라벨 적용: 플러그인 우선, 실패 시 간단 대체 로직
				const applyKoreanLabels = () => {
					const map = mapRef.current;
					if (!map) return;
					const tryPlugin = () => {
						try {
							if (window.MapboxLanguage) {
								const language = new window.MapboxLanguage({
									defaultLanguage: "ko",
								});
								map.addControl(language);
								if (typeof language.setLanguage === "function") {
									language.setLanguage("ko");
								}
								return true;
							}
						} catch (_) {}
						return false;
					};

					if (!tryPlugin()) {
						// 간단 대체: name_ko가 있으면 우선 사용하도록 텍스트 필드 교체
						try {
							const styleObj = map.getStyle();
							if (styleObj?.layers) {
								for (const layer of styleObj.layers) {
									if (layer?.layout && layer.layout["text-field"]) {
										map.setLayoutProperty(layer.id, "text-field", [
											"coalesce",
											["get", "name_ko"],
											["get", "name"],
										]);
									}
								}
							}
						} catch (_) {}
					}
				};

				// load 후 적용, 혹은 styledata 시도
				mapRef.current.once("load", () => {
					setMapReady(true);
					applyKoreanLabels();
					// 최초 로드 시점에 즉시 가시 카페 계산
					computeVisibleCafes();
					// 공식 Geocoder 컨트롤 추가 (CDN 로드 후)
					const addGeocoderControl = () => {
						try {
							const token = mapboxglModule.accessToken || "";
							if (!token) return;
							const center = mapRef.current && mapRef.current.getCenter();
							// eslint-disable-next-line no-undef
							const geocoder = new window.MapboxGeocoder({
								accessToken: token,
								mapboxgl: window.mapboxgl,
								language: "ko",
								placeholder: "장소/주소 검색",
								countries: "kr",
								marker: false,
								flyTo: { zoom: 13, speed: 1.2, curve: 1.42 },
								proximity: center
									? { longitude: center.lng, latitude: center.lat }
									: undefined,
							});
							mapRef.current.addControl(geocoder, "top-left");
						} catch (_) {}
					};

					if (!window.MapboxGeocoder) {
						try {
							// CSS 주입
							const link = document.createElement("link");
							link.rel = "stylesheet";
							link.href =
								"https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.1.2/mapbox-gl-geocoder.css";
							document.head.appendChild(link);
						} catch (_) {}
						try {
							const s = document.createElement("script");
							s.src =
								"https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.1.2/mapbox-gl-geocoder.min.js";
							s.async = true;
							s.onload = addGeocoderControl;
							document.head.appendChild(s);
						} catch (_) {}
					} else {
						addGeocoderControl();
					}
				});
				mapRef.current.once("styledata", () => {
					// 언어 플러그인 동적 로드 (CDN)
					if (!window.MapboxLanguage) {
						try {
							const s = document.createElement("script");
							s.src =
								"https://unpkg.com/@mapbox/mapbox-gl-language/dist/mapbox-gl-language.js";
							s.async = true;
							s.onload = applyKoreanLabels;
							document.head.appendChild(s);
						} catch (_) {}
					} else {
						applyKoreanLabels();
					}
				});

				// 컨테이너 크기 변화에 대응
				if ("ResizeObserver" in window && containerEl) {
					resizeObserverRef.current = new ResizeObserver(() => {
						try {
							mapRef.current && mapRef.current.resize();
						} catch (_) {}
					});
					resizeObserverRef.current.observe(containerEl);
				}
			} catch (_) {
				// 모듈/토큰 없으면 조용히 실패 (빈 컨테이너 유지)
			}
		};

		init();

		return () => {
			isMounted = false;
			try {
				if (mapRef.current) {
					try {
						const canvasContainer =
							mapRef.current.getCanvasContainer &&
							mapRef.current.getCanvasContainer();
						if (canvasContainer && mapRef.current.__customWheelHandler) {
							canvasContainer.removeEventListener(
								"wheel",
								mapRef.current.__customWheelHandler,
								{ passive: false }
							);
						}
					} catch (_) {}
					mapRef.current.remove();
					mapRef.current = null;
				}
				if (resizeObserverRef.current && containerEl) {
					try {
						resizeObserverRef.current.unobserve(containerEl);
					} catch (_) {}
					resizeObserverRef.current = null;
				}
			} catch (_) {}
		};
	}, [theme, containerSize.width]);

	// Handle cafe registration button click
	const handleCafeRegistrationClick = () => {
		if (!isLoggedIn) {
			setShowLoginPromptModal(true);
		} else {
			setShowCafeRegistrationModal(true);
		}
	};

	const carouselCafes = useMemo(() => {
		const list = Array.isArray(visibleCafes) ? visibleCafes : [];
		return list.slice(0, 12);
	}, [visibleCafes]);

	return (
		<>
			{/* Cafe count info - Hide in full mode */}
			{!isFull && (
				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary">
						{cafes.length}개의 카페가 등록되어 있습니다
					</Typography>
				</Box>
			)}

			<Box
				sx={{
					width: "100%",
					maxWidth: "100%",
					height: isFull ? "100%" : "auto",
					minWidth: 0,
					overflowX: "hidden",
					overflowY: "hidden",
					boxSizing: "border-box",
					// contain 완화: 측정/리사이즈 전파 이슈 방지
					contain: "paint",
					position: "relative",
					alignSelf: "stretch",
				}}
			>
				<Box
					ref={wrapperRef}
					sx={{
						width: isFull ? "100vw" : "100%",
						maxWidth: isFull ? "100vw" : "100%",
						minWidth: 0,
						position: "relative",
					}}
				>
					<Box
						ref={mapContainerRef}
						sx={{
							width:
								containerSize.width > 0
									? `${containerSize.width}px`
									: isFull
									? "100vw"
									: "100%",
							maxWidth: "100%",
							minWidth: 0,
							height: isFull
								? "calc(100vh - 180px)" // 하단 캐루셀 공간 확보
								: { xs: 320, sm: 400, md: 560, lg: 640, xl: 720 },
							overflow: "hidden",
							position: "relative",
							boxSizing: "border-box",
							"& .mapboxgl-map, & .mapboxgl-canvas-container, & canvas.mapboxgl-canvas":
								{
									width: "100% !important",
									maxWidth: "100% !important",
									boxSizing: "border-box",
									display: "block",
								},
							"& .mapboxgl-control-container": {
								maxWidth: "100%",
								overflow: "hidden",
								pointerEvents: "auto",
							},
							"& .mapboxgl-ctrl-geocoder": {
								maxWidth: { xs: "calc(100% - 16px)", sm: 320 },
								width: { xs: "calc(100% - 16px)", sm: "auto" },
								boxSizing: "border-box",
								marginLeft: 8,
								marginTop: 8,
							},
							...(isFull && {
								position: "fixed",
								top: 0,
								left: 0,
								zIndex: 1,
							}),
						}}
					/>
				</Box>
				{/* Visible cafes carousel - Show in all modes */}
				<Box
					sx={{
						mt: isFull ? 0 : 1.5,
						overflow: "hidden",
						minWidth: 0,
						width: "100%",
						maxWidth: isFull ? "100vw" : "100%",
						minHeight: { xs: 120, sm: 140, md: 160 },
						boxSizing: "border-box",
						contain: "layout style",
						...(isFull && {
							position: "fixed",
							bottom: 20,
							left: 0,
							right: 0,
							zIndex: 1000,
							backgroundColor: "rgba(255, 255, 255, 0.95)",
							backdropFilter: "blur(10px)",
							borderTop: "1px solid rgba(0, 0, 0, 0.1)",
							px: 2,
							py: 1.5,
						}),
					}}
				>
					<MultiCarousel
						items={carouselCafes}
						visible={{ xs: 2, sm: 2, md: 3, lg: 4, xl: 5 }}
						gap={8}
						step={1}
						renderItem={(c) => (
							<CafeCard cafe={c} onMore={() => setDetailCafe(c)} />
						)}
					/>
				</Box>
			</Box>

			{/* Registration CTA Section - Show as floating buttons in full mode */}
			{isFull ? (
				<Box
					sx={{
						position: "fixed",
						bottom: 250, // 더 위로 올림
						right: 24,
						zIndex: 1001,
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
					}}
				>
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={handleCafeRegistrationClick}
						size="large"
						sx={{
							borderRadius: 2,
							px: 3,
							py: 1.5,
							boxShadow: 3,
						}}
					>
						카페 등록
					</Button>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => window.open("/monthly-threads/2025-09", "_blank")}
						size="large"
						sx={{
							borderRadius: 2,
							px: 3,
							py: 1.5,
							boxShadow: 3,
							backgroundColor: "rgba(255, 255, 255, 0.9)",
							backdropFilter: "blur(10px)",
						}}
					>
						9월호 보러가기
					</Button>
				</Box>
			) : (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						mt: 3,
						mb: 2,
					}}
				>
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={handleCafeRegistrationClick}
						size="large"
						sx={{
							borderRadius: 2,
							px: 4,
							py: 1.5,
						}}
					>
						카페 등록하기
					</Button>
				</Box>
			)}

			{/* Modals */}
			<CafeDetailModal
				opened={!!detailCafe}
				onClose={() => setDetailCafe(null)}
				cafe={detailCafe}
			/>

			<CafeLoginPromptModal
				open={showLoginPromptModal}
				onClose={() => setShowLoginPromptModal(false)}
				isMobile={false} // You can add mobile detection logic if needed
			/>

			<CafeRegistrationModal
				open={showCafeRegistrationModal}
				onClose={() => setShowCafeRegistrationModal(false)}
				currentUser={currentUser}
				isMobile={false} // You can add mobile detection logic if needed
			/>

			{/* Zoom level info - Hide in full mode */}
			{!isFull && (
				<Box sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
					{zoomLevel != null ? `Zoom: ${zoomLevel.toFixed(2)}` : "Zoom: -"}
				</Box>
			)}
		</>
	);
};

export default ThreadsCafeMapSection;
